import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { css } from '@linaria/core'
import { i18n } from '@fs/zion-locale'
import { useTranslation, Trans } from 'react-i18next'
import { Button, colors, useOverlay, DialogOverlay, Paragraph } from '@fs/zion-ui'
import { useFeatureFlag } from '@fs/zion-flags'
import { Link } from '@fs/zion-router'

const cookiePrefsCss = css`
  a:hover {
    text-decoration: none !important;
  }
`

const updatedButtonCss = css`
  align-items: center;
  color: ${colors.gray100};
  background: none;
  border: none;
  font-size: 12px;
  padding: 0;

  // match footer legal links
  & {
    text-decoration: underline;
  }

  // to match global link style
  &:focus-visible {
    outline: solid currentcolor;
    outline-width: 3px;
    outline-offset: 3px;
    border-radius: 2px;
  }
`
const trustArcId = 'teconsent'
const hasDocument = typeof document !== 'undefined'

const loadScript = (src) => {
  if (hasDocument) {
    const existingScript = document.querySelector(`script[src="${src}"]`)
    if (!existingScript) {
      const tag = document.createElement('script')
      tag.async = true
      tag.src = src
      document.getElementsByTagName('head')[0].appendChild(tag)
    }
  } else {
    console.error('Document is not available - cookie preferences script not loaded')
  }
}

//  trustArc banner needs this injection div
const insertConsentBlackbarElement = () => {
  if (hasDocument) {
    const existingDiv = document.getElementById('consent_blackbar')
    if (!existingDiv) {
      const div = document.createElement('div')
      div.id = 'consent_blackbar'
      document.body.appendChild(div)
    }
  } else {
    console.error('Document is not available - consent blackbar div not inserted')
  }
}

// insert the element that trustArc uses to handle the cookie preferences pops
const insertTeConsentElement = () => {
  if (hasDocument) {
    const existingDiv = document.getElementById(trustArcId)
    if (!existingDiv) {
      const div = document.createElement('div')
      div.id = trustArcId

      // styles directly on the element get removed
      // so do this to hide the clickable element
      const style = document.createElement('style')
      style.innerHTML = `
        #${trustArcId} {
          display: none !important;
          visibility: hidden !important;
          height: 0px !important;
          width: 0px !important;
          position: absolute !important;
          overflow: hidden !important;
        }
      `
      document.head.appendChild(style)
      document.body.appendChild(div)
    }
  } else {
    console.error('Document is not available - teconsent element not inserted')
  }
}

// click the link that is injected inside of the `teconsent` element
const handleTrustArcClick = () => {
  const trustArcElement = document.getElementById(trustArcId)
  const injectedClickableElement = trustArcElement?.querySelector('a')
  injectedClickableElement?.click()
  return injectedClickableElement
}

const CookiePrefsButton = ({ btnName = 'footer_cookie_preferences', text, onClick, includeId }) => {
  return (
    <div className={cookiePrefsCss}>
      <Button
        data-testid="cookiePreferences"
        onClick={includeId ? undefined : onClick}
        id={includeId ? trustArcId : undefined}
        linkName={btnName}
        tabIndex="-1"
      >
        {includeId ? null : text}
      </Button>
    </div>
  )
}

const UpdatedCookiePrefsButton = ({ text, onClick, includeId, ariaLabel }) => {
  return (
    <button
      className={updatedButtonCss}
      onClick={includeId ? undefined : onClick}
      data-testid="cookiePreferences"
      type="button"
      id={includeId ? trustArcId : undefined}
      aria-haspopup="dialog"
      aria-label={ariaLabel}
    >
      {includeId ? null : text}
    </button>
  )
}

export default function CookiePreferences({ includeButton = true, updatedButton = false, onClick, btnName }) {
  const [t] = useTranslation()
  const buttonText = t('footer.cookie-preferences', 'Cookie Preferences')
  const ariaLabel = t(
    'footer.cookie-preferences-aria-label',
    'Cookie Preferences, opens a dedicated popup modal window'
  )
  const injectCookiePrefs = useFeatureFlag('home_footer_inject_cookie_prefs_button').isOn
  const errorOverlay = useOverlay()
  errorOverlay.closeOnClickAway = false

  useEffect(() => {
    // TrustArc cookie prefs banner and overlay
    let trusteLang = i18n.language
    if (trusteLang === 'zh') {
      trusteLang = 'ZH-Hant'
    } else if (trusteLang === 'zh-hans') {
      trusteLang = 'zh-cn'
    }
    const scriptUrl = `//consent.trustarc.com/notice?domain=familysearchv2.org&c=teconsent&js=nj&noticeType=bb&text=true&gtm=1&pcookie&pn=1-0&cdn=1&language=${trusteLang}`

    if (injectCookiePrefs) {
      insertTeConsentElement()
    }
    insertConsentBlackbarElement()
    loadScript(scriptUrl)
  }, [injectCookiePrefs])

  // If the flag is on, the trustarc element will be injected into the body
  // click it, if it isn't found, open the error overlay
  const handleClick = () => {
    if (onClick) {
      onClick()
    }

    const openedTrustArcDialog = handleTrustArcClick()
    if (!openedTrustArcDialog) {
      errorOverlay.open()
      console.error('Unable to open TrustArc dialog')
    }
  }

  return (
    <>
      {includeButton &&
        (updatedButton ? (
          <UpdatedCookiePrefsButton
            includeId={!injectCookiePrefs}
            text={buttonText}
            onClick={handleClick}
            ariaLabel={ariaLabel}
          />
        ) : (
          <CookiePrefsButton includeId={!injectCookiePrefs} btnName={btnName} text={buttonText} onClick={handleClick} />
        ))}
      <DialogOverlay
        data-testid="cookie-error-dialog"
        {...errorOverlay}
        title={t('footer.cookie-preferences-error', 'Oops! Something went wrong')}
      >
        <Paragraph size="sm">
          {t('footer.cookie-preferences-error-msg', 'We were unable to load the cookie preferences.')}
        </Paragraph>
        <Paragraph size="sm">
          {t('footer.cookie-preferences-error-msg-refresh', 'Please try again in a moment or refresh the page.')}
        </Paragraph>
        <Paragraph size="sm">
          <Trans
            i18nKey="footer.cookie-preferences-error-support"
            defaults="If the problem persists, please <0>contact support</0>."
            components={[<Link to="/help" key="link" />]}
          />
        </Paragraph>
      </DialogOverlay>
    </>
  )
}

CookiePreferences.propTypes = {
  // Whether to inlcude the cookie prefs button provided. True by default. If false, you must create your own link/button, see docs
  includeButton: PropTypes.bool,

  // Whether to include the updated cookie prefs button provided
  // matches the new thin footer/link style
  updatedButton: PropTypes.bool,

  // Cookie prefs button analytics linkName, default is 'footer_cookie_preference'
  btnName: PropTypes.string,

  // A callback function when the link is clicked, e.g. for analytics
  onClick: PropTypes.func,
}
